import type { TeamTheme } from '@mondy/shared';
import { getAmbientLandmark } from '@mondy/shared';
import { FlagPattern } from './FlagPattern';

/**
 * 6-layer ambient team backdrop. Faithful port of
 * `design_handoff_team_skins/README.md` lines 117-134 and `app.css` `.bg-*`
 * selectors (lines 171-240). Each layer multiplies its base opacity by the
 * `intensity` prop so callers can dial the effect down on smaller cards.
 *
 * Renders nothing when `theme` is null — safe to drop into any container.
 * Callers must size the parent via `position: relative; overflow: hidden`.
 *
 * NOTE: colors come from `theme` directly (not CSS vars) so a scoped use
 * inside the Profile card can ignore whatever global takeover may or may
 * not be active — it always paints the palette passed in.
 */
export function TeamBackdrop({
  theme,
  intensity = 0.6,
  showLandmark = true,
}: {
  theme: TeamTheme | null | undefined;
  /** 0–1 strength multiplier. Default 0.6 matches the design canvas. */
  intensity?: number;
  /** When false, suppresses the landmark silhouette layer. Defaults to true. */
  showLandmark?: boolean;
}) {
  if (!theme) return null;
  const { palette } = theme;
  // Light-mode teams have a pale `deep` that blows out the pane — fall back
  // to `ink` (or a neutral near-black) exactly like `MBg` does in
  // /tmp/mondy-design/skins/mondy-v2/project/mobile-canvas.jsx lines 34-36.
  const isLight = palette.mode === 'light';
  const bgBase = isLight ? palette.ink ?? '#0b0b0d' : palette.deep;

  const landmark = getAmbientLandmark(theme.landmark ?? theme.code);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {/* Layer 1 — deep radial wash */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(140% 80% at 10% -10%, ${palette.primary} 0%, transparent 42%),
            radial-gradient(120% 90% at 100% 110%, ${palette.accent} 0%, transparent 50%),
            linear-gradient(180deg, ${bgBase} 0%, var(--bg) 70%)`,
          opacity: (isLight ? 0.32 : 0.5) * intensity,
        }}
      />
      {/* Layer 2 — top stadium scan */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 180,
          background: `linear-gradient(180deg, ${palette.primary} 0%, transparent 100%)`,
          opacity: 0.1 * intensity,
          mixBlendMode: 'screen',
        }}
      />
      {/* Layer 3 — huge landmark silhouette anchored bottom-right */}
      {showLandmark ? (
        <div
          style={{
            position: 'absolute',
            right: '-5%',
            bottom: '-5%',
            width: '76%',
            height: '80%',
            opacity: 0.18 * intensity,
            mixBlendMode: 'screen',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            overflow: 'hidden',
          }}
        >
          {landmark ? (
            <svg
              viewBox={landmark.viewBox}
              preserveAspectRatio="xMaxYMax meet"
              style={{
                width: '100%',
                height: '100%',
                color: palette.primary,
                filter: 'blur(0.4px)',
              }}
              dangerouslySetInnerHTML={{ __html: landmark.svg }}
            />
          ) : null}
        </div>
      ) : null}
      {/* Layer 4 — pattern tile top-right */}
      {theme.pattern ? (
        <div
          style={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 360,
            height: 360,
            opacity: 0.09 * intensity,
            transform: 'rotate(-8deg)',
            mixBlendMode: 'screen',
            borderRadius: 24,
            overflow: 'hidden',
          }}
        >
          <FlagPattern spec={theme.pattern} />
        </div>
      ) : null}
      {/* Layer 5 — grain */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.05,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .7 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          mixBlendMode: 'overlay',
        }}
      />
      {/* Layer 6 — flag-gradient left edge */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 6,
          background: `linear-gradient(180deg, ${palette.primary}, ${palette.accent})`,
          opacity: 0.8 * intensity,
        }}
      />
    </div>
  );
}
